import {
  ConsumerLogs,
  Execution,
  ExecutionBranches,
  Statistics,
  WithoutFamilyPlan,
} from "interfaces";
import { PlanReloadTone } from "enums";

/**
 * Agregações da Recarga de Planos — TUDO fora do componente (spec 036).
 *
 * Lógica dentro do `render` de uma `Table.Column` é inalcançável pela suíte (lição do QA da
 * spec 035), e é justamente a fiação entre contadores, desfechos e back-compat que pode quebrar.
 * Nada aqui importa React.
 */

export interface BranchRow extends Execution {
  bo_id: string;
}

/**
 * "Falha" continua sendo `error | partial` (spec 033, RN11). `empty` NUNCA entra: uma filial em
 * que o SIGO respondeu com sucesso e sem planos não é falha (desde a spec 062 ela é zerada).
 * Mesma separação vale para "sem família" (spec 035, RN02).
 */
export const isFailure = (row: BranchRow): boolean =>
  row.outcome === "error" || row.outcome === "partial";

export const isEmpty = (row: BranchRow): boolean => row.outcome === "empty";

/** Planos antigos no ar (spec 062): nem troca nem limpeza commitaram — o sinal mais grave. */
export const isStale = (row: BranchRow): boolean => row.shelf_state === "stale";

/** Recortes da tabela de filiais. */
export type RowFilter = "all" | "fail" | "empty" | "nofamily";

export const branchRows = (record?: ConsumerLogs): BranchRow[] => {
  const executions = record?.statistics?.executions ?? {};
  return Object.keys(executions).map((bo_id) => ({
    bo_id,
    ...executions[bo_id],
  }));
};

// ---------------------------------------------------------------------------
// Planos sem família (spec 035)
// ---------------------------------------------------------------------------

export interface WithoutFamilyGroup {
  key: string;
  base_plan: string;
  nm_plan: string;
  branches: string[];
}

export interface WithoutFamilySummary {
  byBranch: Record<string, WithoutFamilyPlan[]>;
  consolidated: WithoutFamilyGroup[];
  totalPlans: number;
}

/**
 * Agrupa por `base_plan + nm_plan`, não só por `base_plan` (RN07 da 035): o mesmo código com
 * nomes diferentes entre filiais é divergência de catálogo e não pode ser mascarada.
 */
export const aggregateWithoutFamily = (
  statistics?: Statistics
): WithoutFamilySummary => {
  const raw = statistics?.without_plan_family;
  // Campo ausente = processo anterior à spec 035, não "zero plano sem família" (RN05 da 035 —
  // mesma lição do QA01 da 033, em que ausência lida como zero gerou falso alarme).
  if (!raw) return { byBranch: {}, consolidated: [], totalPlans: 0 };

  const byBranch: Record<string, WithoutFamilyPlan[]> = {};
  const groups = new Map<string, WithoutFamilyGroup>();

  Object.entries(raw).forEach(([boId, plans]) => {
    const list = Object.values(plans ?? {}).filter(Boolean);
    if (!list.length) return;
    byBranch[boId] = [...list].sort((a, b) =>
      (a.nm_plan ?? "").localeCompare(b.nm_plan ?? "")
    );
    list.forEach((plan) => {
      const key = `${plan.base_plan}|${plan.nm_plan}`;
      const group = groups.get(key);
      if (group) {
        if (!group.branches.includes(boId)) group.branches.push(boId);
        return;
      }
      groups.set(key, {
        key,
        base_plan: plan.base_plan,
        nm_plan: plan.nm_plan,
        branches: [boId],
      });
    });
  });

  // `Array.from` e não spread: o target de compilação do Portal (ES5) não itera
  // `IterableIterator` sem `--downlevelIteration`.
  const consolidated = Array.from(groups.values());
  // Corpo em bloco: `sort` muta no lugar, e devolvê-lo do corpo implícito da arrow sugeriria
  // (falsamente) que uma cópia ordenada está sendo retornada.
  consolidated.forEach((group) => {
    group.branches.sort((a, b) => a.localeCompare(b));
  });
  // Mais filiais afetadas primeiro: é o plano cuja palavra-chave rende mais ao ser cadastrada.
  consolidated.sort(
    (a, b) =>
      b.branches.length - a.branches.length ||
      (a.nm_plan ?? "").localeCompare(b.nm_plan ?? "")
  );

  return { byBranch, consolidated, totalPlans: consolidated.length };
};

/**
 * O número exibido vem do DETALHE quando ele existe (RN04 da 035) — é o que o operador abre ao
 * expandir a linha, então tabela e expansão nunca se contradizem. Sem detalhe (processo antigo),
 * cai para o contador gravado; se o contador for MAIOR que o detalhe, a divergência é sinalizada,
 * não escondida (RN06 da 035).
 */
export const withoutFamilyDisplay = (
  counted: number,
  detailCount: number
): { value: number | null; tooltip: string | null } => {
  if (!detailCount) return { value: counted || null, tooltip: null };
  if (counted > detailCount) {
    return {
      value: detailCount,
      tooltip: `${counted} ocorrências registradas · ${detailCount} ${
        detailCount === 1 ? "plano detalhado" : "planos detalhados"
      }`,
    };
  }
  return { value: detailCount, tooltip: null };
};

/** Nº de planos sem família da filial, na mesma fonte da coluna (RN04 da 035). */
export const branchWithoutFamilyCount = (
  row: BranchRow,
  summary: WithoutFamilySummary
): number =>
  withoutFamilyDisplay(
    row.without_family ?? 0,
    (summary.byBranch[row.bo_id] ?? []).length
  ).value ?? 0;

// ---------------------------------------------------------------------------
// Resumo do processo
// ---------------------------------------------------------------------------

export interface ProcessSummary {
  total: number;
  finalized: number;
  failures: number;
  emptyBranches: number;
  /** Filiais com planos antigos no ar (spec 062). */
  staleBranches: number;
  totalPlans: number;
  withoutFamilyBranches: number;
  avgTime: number;
  maxTime: number;
  progress: number;
  /** Alguma filial reporta `plans_count`? Sem isso, "zero plano" é ausência de dado, não zero. */
  reportsPlans: boolean;
  /** Processo finalizado que não coletou um único plano (spec 033, RN12). */
  noPlans: boolean;
  done: boolean;
}

export const summarize = (
  rows: BranchRow[],
  lastStatus?: string,
  withoutFamily?: WithoutFamilySummary
): ProcessSummary => {
  const done = lastStatus === "finalizado";
  const total = rows.length;
  const finalized = rows.filter((row) => row.status === "finalizado").length;
  const failures = rows.filter(isFailure).length;
  const emptyBranches = rows.filter(isEmpty).length;
  const staleBranches = rows.filter(isStale).length;
  const totalPlans = rows.reduce((sum, row) => sum + (row.plans_count ?? 0), 0);
  const times = rows
    .map((row) => row.exec_time ?? 0)
    .filter((time) => time > 0);
  const avgTime = times.length
    ? times.reduce((a, b) => a + b, 0) / times.length
    : 0;
  const maxTime = times.length ? Math.max(...times) : 0;

  const summary = withoutFamily ?? { byBranch: {}, consolidated: [], totalPlans: 0 };
  const withoutFamilyBranches = rows.filter(
    (row) => branchWithoutFamilyCount(row, summary) > 0
  ).length;

  // Uma recarga que termina sem um único plano nunca é legítima (spec 033, RN12). Só vale com o
  // processo finalizado (durante o polling o total legitimamente começa em zero) e só quando
  // alguma filial de fato REPORTA `plans_count`: em docs gravados antes da spec 027 o campo nem
  // existe (é opcional por back-compat), e ausência de dado não é zero plano (QA01 da 033).
  const reportsPlans = rows.some((row) => row.plans_count !== undefined);

  return {
    total,
    finalized,
    failures,
    emptyBranches,
    staleBranches,
    totalPlans,
    withoutFamilyBranches,
    avgTime,
    maxTime,
    progress: total ? Math.round((finalized / total) * 100) : 0,
    reportsPlans,
    noPlans: done && total > 0 && reportsPlans && totalPlans === 0,
    done,
  };
};

/**
 * Precedência de cor do processo (spec 033, RN12), inalterada pelo redesign:
 * falha → sem planos → concluído → em andamento.
 */
export const processTone = (summary: {
  failures: number;
  noPlans: boolean;
  done: boolean;
}): PlanReloadTone => {
  if (summary.failures) return "error";
  if (summary.noPlans) return "warning";
  if (summary.done) return "success";
  return "running";
};

/**
 * Estimativa de conclusão em segundos, ou `null` quando não há amostra suficiente (RN12 da 036).
 * Sem pelo menos três filiais concluídas o número é adivinhação — e adivinhação exibida como
 * estimativa é pior que ausência.
 */
export const MIN_SAMPLE_FOR_ESTIMATE = 3;

export const estimateRemaining = (summary: {
  total: number;
  finalized: number;
  avgTime: number;
  done: boolean;
}): number | null => {
  if (summary.done) return null;
  if (summary.finalized < MIN_SAMPLE_FOR_ESTIMATE) return null;
  const remaining = summary.total - summary.finalized;
  if (remaining <= 0 || summary.avgTime <= 0) return null;
  return remaining * summary.avgTime;
};

// ---------------------------------------------------------------------------
// Ramos de preço (spec 008)
// ---------------------------------------------------------------------------

export interface RamoDefinition {
  key: keyof ExecutionBranches;
  label: string;
  hint: string;
}

/**
 * Ordem de PRIORIDADE da spec 008 (incondicional antigo > padrão antigo > campanha nova > bruto),
 * não ordem alfabética: é ela que dá sentido ao dado — um salto em "Bruto" significa plano sem
 * desconto vigente.
 *
 * A classificação vem pronta do Plan.Producer (`branches` do consumer_log), onde a campanha nova
 * vence o coalesce legado quando há código de campanha com preço; aqui só rotulamos.
 */
export const RAMOS: RamoDefinition[] = [
  {
    key: "incond_old",
    label: "Incondicional (antigo)",
    hint: "incond_old — desconto incondicional da tabela antiga, sem campanha nova vigente",
  },
  {
    key: "standard_old",
    label: "Padrão (antigo)",
    hint: "standard_old — líquido padrão da tabela antiga",
  },
  {
    key: "campaign_new",
    label: "Campanha nova",
    hint: "campaign_new — cd_campaign_discount com preço de 1ª parcela da campanha",
  },
  {
    key: "gross",
    label: "Bruto",
    hint: "gross — nenhum desconto vigente (inclui código de campanha sem preço)",
  },
];

export interface RamoTotal extends RamoDefinition {
  count: number;
  percent: number;
}

export interface RamoSummary {
  ramos: RamoTotal[];
  total: number;
  /** Nenhuma filial gravou `branches` (processo anterior à spec 027) — não é "zero plano". */
  reported: boolean;
}

export const aggregateBranches = (rows: BranchRow[]): RamoSummary => {
  const reported = rows.some((row) => row.branches !== undefined);
  const counts = RAMOS.map((ramo) =>
    rows.reduce((sum, row) => sum + (row.branches?.[ramo.key] ?? 0), 0)
  );
  const total = counts.reduce((a, b) => a + b, 0);

  return {
    reported,
    total,
    ramos: RAMOS.map((ramo, index) => ({
      ...ramo,
      count: counts[index],
      percent: total ? (counts[index] / total) * 100 : 0,
    })),
  };
};

// ---------------------------------------------------------------------------
// Sinais do processo para a listagem (coluna "Atenção")
// ---------------------------------------------------------------------------

export interface ProcessSignals {
  failures: number;
  emptyBranches: number;
  withoutFamilyPlans: number;
  totalPlans: number;
  finalized: number;
  total: number;
  reportsPlans: boolean;
  noPlans: boolean;
  progress: number;
}

/**
 * Sinais de um documento da LISTAGEM. Sai de graça porque o Admin.Api devolve os documentos sem
 * projeção e o Bff repassa verbatim — `statistics` inteiro já chega na lista.
 */
export const processSignals = (record: ConsumerLogs): ProcessSignals => {
  const rows = branchRows(record);
  const withoutFamily = aggregateWithoutFamily(record?.statistics);
  const summary = summarize(rows, record?.last_status, withoutFamily);

  return {
    failures: summary.failures,
    emptyBranches: summary.emptyBranches,
    withoutFamilyPlans: withoutFamily.totalPlans,
    totalPlans: summary.totalPlans,
    finalized: summary.finalized,
    total: summary.total,
    reportsPlans: summary.reportsPlans,
    noPlans: summary.noPlans,
    progress: summary.progress,
  };
};

/** Há processo não finalizado na página corrente? Alimenta o aviso do topo do hub. */
export const findRunningProcess = (
  records?: ConsumerLogs[]
): ConsumerLogs | undefined =>
  (records ?? []).find((record) => record.last_status !== "finalizado");

// ---------------------------------------------------------------------------
// Formatação de número exibido
// ---------------------------------------------------------------------------

/**
 * Ausência ≠ zero (RN05 da 036 / QA01 da 033). Um `-` diz "não foi registrado"; `0` diz
 * "registrado e vale zero". Trocar um pelo outro já gerou falso alarme nesta tela.
 */
export const metricNumber = (
  value: number | undefined | null,
  reported = true
): string => {
  if (!reported || value === undefined || value === null) return "-";
  return value.toLocaleString("pt-BR");
};
